(() => {
  if (window.FXLProductMotion) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const galleries = new WeakMap();
  const armedGalleries = new WeakSet();
  const stickyStates = new WeakMap();

  function createGallery(stage) {
    if (galleries.has(stage)) return galleries.get(stage);
    const root = stage.closest('[data-mry-product]');
    const buttons = [...root.querySelectorAll('[data-media-id]')];
    const original = stage.querySelector('.mry-pdp__main-image');
    if (!original || buttons.length < 2) return null;
    const imageWidth = Number(original.getAttribute('width')) || 1600;
    const imageHeight = Number(original.getAttribute('height')) || 1600;
    let current = Math.max(0, buttons.findIndex(button => button.classList.contains('is-selected')));
    let request = 0;
    let programmed = false;
    let userScrolling = false;
    let jumpVersion = 0;
    let fade = null;
    let ghost = null;
    let settleTimer = 0;
    const setImageLoading = value => stage.setAttribute('aria-busy', String(value));
    const renderedTrackCandidate = stage.querySelector(':scope > .fxl-media-track');
    const renderedTrack = renderedTrackCandidate?.classList?.contains('fxl-media-track') ? renderedTrackCandidate : null;
    const renderedSlide = original.closest
      ? original.closest('.fxl-media-slide')
      : original.parentElement?.classList?.contains('fxl-media-slide') ? original.parentElement : null;
    const track = renderedTrack || document.createElement('div');
    track.className = 'fxl-media-track';
    track.tabIndex = 0;
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Product images');
    const slides = [];
    const images = buttons.map((button, index) => {
      const slide = index === current && renderedSlide ? renderedSlide : document.createElement('div');
      slide.className = 'fxl-media-slide';
      const image = index === current ? original : new Image();
      image.alt = button.dataset.mediaAlt || original.alt;
      image.width = imageWidth;
      image.height = imageHeight;
      image.sizes = original.sizes;
      image.draggable = false;
      slide.append(image);
      slides.push(slide);
      return image;
    });
    if (track.replaceChildren) track.replaceChildren(...slides);
    else slides.forEach(slide => track.append(slide));
    stage.classList.add('fxl-media-enhanced');
    if (!renderedTrack) stage.append(track);
    const load = index => {
      const image = images[index];
      if (!image) return;
      if (!image.getAttribute('src')) {
        if (buttons[index].dataset.mediaSrcset) image.srcset = buttons[index].dataset.mediaSrcset;
        image.src = buttons[index].dataset.mediaSrc;
      }
      return image;
    };
    const clearFade = () => {
      fade?.cancel();
      fade = null;
      ghost?.remove();
      ghost = null;
    };
    const activate = index => {
      current = index;
      buttons.forEach((button, i) => {
        button.classList.toggle('is-selected', i === index);
        button.setAttribute('aria-pressed', String(i === index));
        images[i].classList.toggle('mry-pdp__main-image', i === index);
        images[i].parentElement.setAttribute('aria-hidden', String(i !== index));
      });
      load(index - 1);
      load(index);
      load(index + 1);
    };
    const jump = index => {
      const version = ++jumpVersion;
      userScrolling = false;
      window.clearTimeout(settleTimer);
      programmed = true;
      track.scrollTo({ left: index * track.clientWidth, behavior: 'instant' });
      window.requestAnimationFrame(() => { if (version === jumpVersion) programmed = false; });
    };
    const select = async mediaId => {
      const index = buttons.findIndex(button => button.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      const token = ++request;
      userScrolling = false;
      window.clearTimeout(settleTimer);
      if (index === current) { setImageLoading(false); jump(index); return; }
      setImageLoading(true);
      const image = load(index);
      try { await image.decode(); } catch { if (token === request) setImageLoading(false); return; }
      if (token !== request || !stage.isConnected) return;
      setImageLoading(false);
      clearFade();
      const oldImage = images[current];
      if (!reduced.matches && oldImage.complete && oldImage.naturalWidth) {
        ghost = oldImage.cloneNode();
        ghost.className = 'fxl-media-previous';
        ghost.setAttribute('aria-hidden', 'true');
        stage.append(ghost);
      }
      jump(index);
      activate(index);
      if (ghost) {
        const previous = ghost;
        fade = previous.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-out' });
        fade.onfinish = () => { previous.remove(); if (ghost === previous) ghost = null; };
      }
    };
    const settle = () => {
      if (programmed || !userScrolling || !track.clientWidth) return;
      const index = Math.max(0, Math.min(images.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
      activate(index);
    };
    track.addEventListener('scroll', () => {
      if (programmed || !userScrolling) return;
      const near = Math.round(track.scrollLeft / track.clientWidth);
      load(near - 1);
      load(near);
      load(near + 1);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, 120);
    }, { passive: true });
    track.addEventListener('scrollend', settle);
    // Native scrolling follows the finger and preserves vertical page scrolling and pinch zoom.
    track.addEventListener('pointerdown', () => { userScrolling = true; request += 1; setImageLoading(false); clearFade(); }, { passive: true });
    track.addEventListener('wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) { userScrolling = true; request += 1; setImageLoading(false); clearFade(); } }, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const index = Math.max(0, Math.min(buttons.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1)));
      select(buttons[index].dataset.mediaId);
    });
    const resizeObserver = new ResizeObserver(() => jump(current));
    resizeObserver.observe(stage);
    const onReducedMotion = () => { if (reduced.matches) clearFade(); };
    reduced.addEventListener('change', onReducedMotion);
    activate(current);
    jump(current);
    const controller = {
      select,
      destroy() {
        request += 1;
        setImageLoading(false);
        clearFade();
        window.clearTimeout(settleTimer);
        resizeObserver.disconnect();
        reduced.removeEventListener('change', onReducedMotion);
        galleries.delete(stage);
      },
    };
    galleries.set(stage, controller);
    return controller;
  }

  function setVisible(bar, visible) {
    if (!bar) return;
    let state = stickyStates.get(bar);
    if (!state) {
      state = { visible: null, timer: 0, frame: 0, end: null };
      stickyStates.set(bar, state);
    }
    if (state.visible === visible) return;
    state.visible = visible;
    window.clearTimeout(state.timer);
    window.cancelAnimationFrame(state.frame);
    if (state.end) bar.removeEventListener('transitionend', state.end);
    state.end = null;
    bar.inert = !visible;
    if (visible) {
      const wasHidden = bar.hidden;
      bar.hidden = false;
      if (wasHidden && !reduced.matches) {
        // Establish the starting style once after leaving display:none.
        void bar.offsetHeight;
        state.frame = window.requestAnimationFrame(() => bar.classList.add('is-visible'));
      } else bar.classList.add('is-visible');
      return;
    }
    bar.classList.remove('is-visible');
    const finish = () => {
      window.clearTimeout(state.timer);
      if (state.end) bar.removeEventListener('transitionend', state.end);
      state.end = null;
      if (!state.visible) bar.hidden = true;
    };
    state.end = event => { if (event.target === bar && event.propertyName === 'transform') finish(); };
    bar.addEventListener('transitionend', state.end);
    if (reduced.matches) finish();
    else state.timer = window.setTimeout(finish, 240);
  }
  window.FXLProductMotion = {
    select(stage, mediaId) {
      if (!stage) return false;
      const gallery = createGallery(stage);
      if (!gallery) return false;
      gallery.select(mediaId);
      return true;
    },
    setVisible,
  };
  const armGallery = stage => {
    if (armedGalleries.has(stage)) return;
    armedGalleries.add(stage);
    const activate = () => createGallery(stage);
    stage.addEventListener('pointerdown', activate, { once: true, capture: true, passive: true });
    stage.addEventListener('keydown', event => {
      if (['ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(event.key)) activate();
    }, { once: true });
  };
  const init = () => document.querySelectorAll('[data-swipe-stage]').forEach(armGallery);
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-swipe-stage]').forEach(stage => galleries.get(stage)?.destroy());
  });
})();
